import { getConfig } from "./config";
import type {
  BoxItem,
  BoxTier,
  PremiumItem,
  TissuePaper,
} from "./types";

// The widget is loaded same-origin with WordPress, so all REST calls are
// relative paths. In `npm run dev`, Vite proxies these to VITE_WP_URL.

async function getJson<T>(path: string): Promise<T> {
  const url = `/wp-json/biab/v1${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `GET ${path} failed: ${res.status}${text ? ` - ${text}` : ""}`,
    );
  }
  return (await res.json()) as T;
}

export function fetchBoxItems(): Promise<BoxItem[]> {
  return getJson<BoxItem[]>("/items");
}

export function fetchPremiumItems(): Promise<PremiumItem[]> {
  return getJson<PremiumItem[]>("/premium");
}

export function fetchTissuePapers(): Promise<TissuePaper[]> {
  return getJson<TissuePaper[]>("/tissue");
}

export function fetchBoxTiers(): Promise<BoxTier[]> {
  return getJson<BoxTier[]>("/tiers");
}

export interface AddToCartPayload {
  tierVariationId: number;
  items: Array<{ id: number; quantity: number }>;
  premium: number[];
  tissueId: number;
}

const STORE_API = "/wp-json/wc/store/v1";

/**
 * Ask the Store API for a currently-valid nonce.
 *
 * GET /cart doesn't require a nonce, and Woo attaches a fresh `Nonce` header
 * to every cart response. This is the escape hatch when the nonce embedded in
 * the page is missing or stale — the latter is common in production, where a
 * full-page cache can serve HTML whose nonce was minted for a different
 * session (or has since expired).
 */
async function fetchFreshNonce(): Promise<string | null> {
  try {
    const res = await fetch(`${STORE_API}/cart`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    return res.headers.get("Nonce");
  } catch {
    return null;
  }
}

function postAddItem(body: unknown, nonce: string): Promise<Response> {
  return fetch(`${STORE_API}/cart/add-item`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      // Woo reads the `Nonce` header. `X-WC-Store-API-Nonce` is only honoured
      // by Woo < 8.x, so send both to stay compatible either way.
      ...(nonce ? { Nonce: nonce, "X-WC-Store-API-Nonce": nonce } : {}),
    },
    body: JSON.stringify(body),
  });
}

export async function addBoxToCart(payload: AddToCartPayload): Promise<void> {
  const body = {
    id: Number(payload.tierVariationId),
    quantity: 1,
    extensions: {
      biab_box: {
        items: payload.items,
        premium: payload.premium,
        tissue_id: Number(payload.tissueId),
      },
    },
  };

  let nonce = getConfig().nonce || (await fetchFreshNonce()) || "";
  let res = await postAddItem(body, nonce);

  // 401 = nonce header missing, 403 = nonce rejected. Both are recoverable by
  // minting a fresh one and replaying the request exactly once.
  if (res.status === 401 || res.status === 403) {
    const fresh = await fetchFreshNonce();
    if (fresh && fresh !== nonce) {
      nonce = fresh;
      res = await postAddItem(body, nonce);
    }
  }

  if (!res.ok) {
    let message = `Store API ${res.status}`;
    try {
      const data = (await res.json()) as { message?: string };
      if (data?.message) {
        message = data.message;
      }
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}
